/*
 * Copyright (c) 2024 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback, useDeferredValue, useEffect, useMemo, useRef} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import debounce from 'lodash.debounce';
import {useRouter} from 'next/compat/router';
import {getAccountIds, hasAccountIds} from '@ion/core/src/entities/Accounts';
import {useHasMounted} from '@ion/core/src/hooks/useHasMounted';
import {useNoop} from '@ion/core/src/hooks/usenoop';
import {entityId} from '@ion/core/src/services/ConnectUtils';
import {FindNearbyType} from '@ion/core/src/types/entities';
import {isFalse} from '@ion/core/src/utils/BooleanUtils';
import {hideElementById} from '@ion/core/src/utils/BrowserUtils';
import RootManager from '@ion/dls/src/client/RootManager';
import {hasGeofenceChanged} from '@ion/dls/src/maps/helpers/geofenceLayer';
import LandmarkGeofenceLayer from '@ion/dls/src/maps/layers/LandmarkGeofenceLayer';
import {isGeoFenceEnabled} from '@ion/dls/src/maps/layers/LayerChooser';
import {MapContainerProps} from '@ion/dls/src/maps/types/types';
import {TrackingMode} from '@ion/dls/src/types/types';
import {hasLandmarkGeofenceAccess} from '@ion/ion-suite/src/security/Features';
import QueryKey from '@ion/query/src/config/QueryKeys';
import {useLoadGeofences} from '@ion/query/src/hooks/useLoadGeofences';
import {useLoadGeofenceUpdates} from '@ion/query/src/hooks/useLoadGeofenceUpdates';
import {Route} from '@/routes/RouteDefinitions';
import {
  findNearby,
  resetGeofenceStatus,
  setFindButtonClickStatus,
  setFindNearbyFromItem,
  setFindNearbyPanelActive,
  setGeofenceLayerOpacity,
  setGeofenceLoaded,
  setTrackingMapLayers
} from '@/store/actions/tracking';
import {isCurrentPage} from '@/store/helpers';
import {
  accountsSelector,
  globalCurrentGroupSelector,
  globalGroupAncestorAccountIdsSelector,
  globalGroupIdSelector
} from '@/store/selectors/global-group-chooser-selectors';
import {userSelector} from '@/store/selectors/login-selectors';
import {
  findNearbySelector,
  geofenceBorderOnlySelector,
  geofenceLabelSelector,
  geofenceOpacitySelector,
  geofenceStatusSelector,
  isGeofenceLoadedSelector,
  mapLayerSelector,
  savedGeofenceSelector,
  savingGeofenceSelector,
  searchResultSelector,
  selectedGeoFencesSelector,
  showPopupSelector,
  trackingModeSelector
} from '@/store/selectors/tracking-selectors';
import {getImmediateSubAccountIds, isImmediateSubAccountsOnly} from '@/store/subAccounts';
import {getFindNearbyGeofence} from '../../../helpers';
import {useTrackedFleetUpdate} from '../../../hooks/useTrackedFleetUpdate';

interface GeofenceLayerContainerProps extends MapContainerProps {
  canAddGeofence?: boolean;
  editGeofenceGraphic?: any;
  setEditGeofenceGraphic?: Function;
};

const GeofenceLayerContainer = ({
  map,
  view,
  canAddGeofence,
  editGeofenceGraphic,
  setEditGeofenceGraphic
}: GeofenceLayerContainerProps) => {
  const isMounted           = useHasMounted(null as any);
  const dispatch            = useDispatch();
  const route               = useRouter();
  const noop                = useNoop();
  const account             = useSelector(globalCurrentGroupSelector);
  const accounts            = useSelector(accountsSelector);
  const user                = useSelector(userSelector);
  const layers              = useSelector(mapLayerSelector);
  const searchResult        = useSelector(searchResultSelector);
  const findLabel           = useSelector(findNearbySelector);
  const savedGeofence       = useSelector(savedGeofenceSelector);
  const savingGeofence      = useSelector(savingGeofenceSelector);
  const geofenceStatus      = useSelector(geofenceStatusSelector);
  const geofenceOpacity     = useSelector(geofenceOpacitySelector);
  const globalGroupId       = useSelector(globalGroupIdSelector);
  const isFindNearbyPopup   = useSelector(showPopupSelector);
  const selectedGeoFences   = useSelector(selectedGeoFencesSelector);
  const geofenceLabelState  = useSelector(geofenceLabelSelector);
  const geofenceBorderOnly  = useSelector(geofenceBorderOnlySelector);
  const ancestorAccountId   = useSelector(globalGroupAncestorAccountIdsSelector);
  const isGeofenceLoaded    = useSelector(isGeofenceLoadedSelector);
  const trackingMode        = useSelector(trackingModeSelector);
  const isGeofenceLoadedRef = useRef(isGeofenceLoaded);

  const isRootAccount    = useMemo(() => account?.name === 'CACTC', [account]);
  const isGeofenceAccess = hasLandmarkGeofenceAccess();

  const isTrackingBetaActive = useMemo(() =>
    isCurrentPage(route?.route, Route.TRACKING2) && trackingMode === TrackingMode?.Live
  , [route?.route, trackingMode]);

  // the selected group's hierarchy is only updated once the group change completes,
  // while globalGroupId (the query key) changes as soon as it starts - the query
  // waits for both to match, so it never runs with the previous group's accounts ...
  const isAccountHierarchyCurrent = useMemo(() => (
    Boolean(globalGroupId) && entityId(accounts?.[0]) === globalGroupId
  ), [accounts, globalGroupId]);

  const descendantAccountId = useMemo(() => {
    const accountIds = isImmediateSubAccountsOnly(globalGroupId)
      ? getImmediateSubAccountIds(accounts?.[0], globalGroupId)
      : getAccountIds(null, accounts);
    return accountIds;
  }, [accounts, globalGroupId]);

  const queryEnabled = useMemo(() =>
    isGeoFenceEnabled(layers) &&
      isTrackingBetaActive &&
      isAccountHierarchyCurrent &&
      hasAccountIds(descendantAccountId)
  , [layers, isTrackingBetaActive, isAccountHierarchyCurrent, descendantAccountId]);

  const onResetGeofenceStatus = useCallback(() => {
    setTimeout(() => {
      dispatch(resetGeofenceStatus(null));
    }, 300);
  }, []);

  const onLayersChange = useCallback(function(layers) {
    dispatch(setTrackingMapLayers(layers));
  }, []);
  
  const onFindNearyBy = useCallback((value) => {
    dispatch(findNearby({
      ...value,
      findType : FindNearbyType.geofence
    }) as any);
  }, []);

  const ancestorAccountIds = useMemo(() => {
    let ancestorAccountValue = null;
    let ancestorIds = null;
    if (ancestorAccountId) {
      ancestorAccountValue = Object.values(ancestorAccountId)[0];
      if (ancestorAccountValue) {
        ancestorIds  = Object.keys(ancestorAccountValue).toString();
      }
    }
    return ancestorIds || [];
  }, [ancestorAccountId]);

  const queryParams = useMemo(() => {
    return {
      accountIds         : descendantAccountId,
      ancestorAccountIds : ancestorAccountIds
    };
  }, [descendantAccountId, ancestorAccountIds]);

  const {data: geofenceResults, hasNextPage, hydrateData} = useLoadGeofences({
    queryParams,
    globalGroupId,
    baseKey     : QueryKey.LOAD_GEOFENCES,
    rowsPerPage : 5000,
    enabled     : queryEnabled
  });

  // the layer hydrates the cache from its features on unmount (e.g. when the map
  // remounts for a new web map after a page refresh) - an empty or missing feature
  // list (layer not created yet, or already cleared) would overwrite the loaded
  // geofences with nothing, leaving the new layer empty until the cache expires ...
  const setGeofenceState = useCallback(async(attributes: any[], globalGroupId: number) => {
    if (globalGroupId && attributes?.length) {
      hydrateData(attributes, QueryKey.LOAD_GEOFENCES, globalGroupId);
    }
  }, [hydrateData]);

  useEffect(() => {
    if (isFalse(hasNextPage)) {
      dispatch(setGeofenceLoaded(!hasNextPage));
    }
  }, [hasNextPage]);

  useEffect(() => {
    isGeofenceLoadedRef.current = isGeofenceLoaded;
  }, [isGeofenceLoaded]);

  const geofences = useMemo(() => {
    const {pages} = geofenceResults || {} as any;
    if (pages?.length) {
      if (!isGeofenceLoadedRef.current) {
        return pages[pages.length - 1];
      }
      const flattenedAssets = pages?.flat();
      return flattenedAssets;
    };
    return [];
  }, [geofenceResults]);

  const {
    data: geofenceUpdatesResults,
    refresh,
    // `loading` (createQueryResult's own computed flag) is
    // `isLoading || (!data && isFetching)` - once the first page has ever
    // loaded, `data` is truthy forever after, so `loading` goes permanently
    // false even while a later background refetch is genuinely in flight.
    // The raw `isFetching` (also present on this result via createQueryResult's
    // `{...query, ...result}` spread) reflects every refetch, not just the
    // first - required here since useFleetUpdateFetchingStatus needs to see
    // every refetch, not just the initial load ...
    isFetching: isFetchingGeofenceUpdates,
    isFetchingNextPage: isFetchingNextGeofenceUpdatesPage
  } = useLoadGeofenceUpdates({
    queryParams,
    globalGroupId,
    parentBaseKey : QueryKey.LOAD_GEOFENCES,
    baseKey       : QueryKey.LOAD_GEOFENCE_UPDATES,
    enabled       : isFalse(hasNextPage) && queryEnabled,
    rowsPerPage   : 5000
  });

  // was previously polled independently via refetchInterval, decoupled from
  // every other tracking2 consumer's own 12-second cycle - now reacts to the
  // same shared AssetUpdateProgressBar-driven pulse as the rest. refresh is
  // wrapped (not just mirrored via isFetching) so the bar's shared registry
  // sees this consumer start and finish even if the refetch resolves within
  // the same render batch ...
  useTrackedFleetUpdate('geofence', refresh, isFetchingGeofenceUpdates || isFetchingNextGeofenceUpdatesPage);

  // every page of updates is applied - a refetch replaces all pages at once, so the
  // last page alone would drop the earlier pages' updates ...
  const geofenceUpdatesRaw = useMemo(() => {
    const {pages} = geofenceUpdatesResults || {} as any;
    if (pages?.length) {
      return pages.flat();
    };
    return [];
  }, [geofenceUpdatesResults]);

  // defers the heavy map layer re-render this triggers once react-query's cache updates,
  // since startTransition alone doesn't cover an async cache update landing after the call
  // that started it ...
  const geofenceUpdates = useDeferredValue(geofenceUpdatesRaw);

  useEffect(() => {
    if (hasGeofenceChanged(geofenceStatus)) {
      refresh();
      onResetGeofenceStatus();
    }
  }, [geofenceStatus, refresh]);

  const onFindButtonStateChange = useCallback((status) => {
    dispatch(setFindButtonClickStatus(status));
  }, []);

  const onGeofenceOpacityChange = useCallback(debounce(function(geofenceOpacity) {
    dispatch(setGeofenceLayerOpacity(geofenceOpacity));
  }, 250), []);

  const setFindNearbyActions = useCallback((geofence: __esri.Graphic) => {
    hideElementById('findNearbyTooltip');
    view?.closePopup();
    dispatch(setFindNearbyFromItem(getFindNearbyGeofence(geofence as any)));
    dispatch(setFindNearbyPanelActive(true));
  }, []);

  const layerProps = useMemo(() => ({
    geofences,
    globalGroupId,
    savingGeofence,
    geofenceUpdates,
    setGeofenceState,
    onResetGeofenceStatus
  }), [
    geofences,
    globalGroupId,
    savingGeofence,
    geofenceUpdates,
    setGeofenceState,
    onResetGeofenceStatus
  ]);

  const searchProps = useMemo(() => ({
    user,
    searchResult
  }), [user, searchResult]);

  const layerChooserProps = useMemo(() => ({
    accounts,
    findLabel,
    savedGeofence,
    onFindNearyBy,
    isRootAccount,
    onLayersChange,
    geofenceStatus,
    geofenceOpacity,
    isFindNearbyPopup,
    selectedGeoFences,
    geofenceLabelState,
    ancestorAccountIds,
    geofenceBorderOnly,
    descendantAccountId,
    onFindButtonStateChange,
    onGeofenceOpacityChange,
    onFindNearbyPopupStatusChange   : noop,
    onFindNearbyBreadcrumbBackClick : noop,
    setFindNearbyPopupForGeofence   : setFindNearbyActions
  }), [
    accounts,
    findLabel,
    savedGeofence,
    onFindNearyBy,
    isRootAccount,
    onLayersChange,
    geofenceStatus,
    geofenceOpacity,
    isFindNearbyPopup,
    selectedGeoFences,
    geofenceLabelState,
    ancestorAccountIds,
    geofenceBorderOnly,
    descendantAccountId,
    setFindNearbyActions,
    onFindButtonStateChange,
    onGeofenceOpacityChange
  ]);
  if (isMounted) {
    return (
      <LandmarkGeofenceLayer isTrackingV2
        isGeofenceAccess={isGeofenceAccess}
        map={map}
        view={view}
        layers={layers}
        {...layerProps}
        {...searchProps}
        canAddGeofence={canAddGeofence}
        editGeofenceGraphic={editGeofenceGraphic}
        layerChooserProps={layerChooserProps as any}
        setEditGeofenceGraphic={setEditGeofenceGraphic} />
    );
  }
};
  
export default GeofenceLayerContainer;
